import * as React from "react";
import * as Portal from "@radix-ui/react-portal";
import { GridSidebar } from "@/modules/snapshots/grid/sidebar";
import { BusinessReportOutcome, BusinessReportOutcomeResult } from "@/modules/snapshots/businessReports.types";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { GridSidebarTab } from "@/modules/snapshots/grid/sidebar/tabs";

interface OrdinaryProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  businessReportOutcome: BusinessReportOutcome;
  result?: BusinessReportOutcomeResult;
  initialTab?: GridSidebarTab;
  snapshotCollectionId: string;
  subcollectionId?: string;
  columnName?: string;
  isCitationView?: boolean;
  contractFamilyProjection?: boolean;
  canEditResult?: boolean;
  onSaveResult?: (value: string) => Promise<void>;
  onRevertResult?: () => Promise<void>;
}

type Props = OrdinaryProps;

export const SnapshotGridPortal: React.FC<Props> = props => {
  const {
    result,
    snapshotCollectionId,
    subcollectionId,
    businessReportOutcome,
    isOpen,
    setIsOpen,
    initialTab,
    columnName,
    isCitationView = false,
    contractFamilyProjection = false,
    canEditResult = true,
    onSaveResult,
    onRevertResult,
  } = props;
  const [showPortalContent, setShowPortalContent] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  const showTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const { setCurrentPdfId, setCurrentCitation } = useCurrentPdf();

  const showExtraContent = React.useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    setShowPortalContent(true);
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      setIsAnimating(true);
    }, 10);
  }, []);

  const hideExtraContent = React.useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setIsAnimating(false);
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null;
      setShowPortalContent(false);
      setIsOpen(false);
    }, 300);
  }, [setIsOpen]);

  React.useEffect(
    () => () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    [],
  );

  React.useEffect(() => {
    setCurrentPdfId(isOpen ? (businessReportOutcome.fs_entry?.id ?? null) : null);
    setCurrentCitation(null);
  }, [
    businessReportOutcome.fs_entry?.id,
    businessReportOutcome.id,
    isOpen,
    result?.id,
    setCurrentCitation,
    setCurrentPdfId,
  ]);

  React.useEffect(() => {
    setPortalContainer(document.body);
    return () => {
      setCurrentCitation(null);
      setCurrentPdfId(null);
    };
  }, [setCurrentCitation, setCurrentPdfId]);

  React.useEffect(() => {
    if (isOpen) {
      showExtraContent();
    } else {
      hideExtraContent();
    }
  }, [hideExtraContent, isOpen, showExtraContent]);

  return (
    <>
      {showPortalContent && portalContainer && (
        <Portal.Root container={portalContainer}>
          <GridSidebar
            isAnimating={isAnimating}
            result={result}
            initialTab={initialTab}
            snapshotCollectionId={snapshotCollectionId}
            subcollectionId={subcollectionId}
            businessReportOutcome={businessReportOutcome}
            onClose={hideExtraContent}
            columnName={columnName}
            isCitationView={isCitationView}
            contractFamilyProjection={contractFamilyProjection}
            canEditResult={canEditResult}
            onSaveResult={onSaveResult}
            onRevertResult={onRevertResult}
          />
        </Portal.Root>
      )}
    </>
  );
};
